<script setup lang="ts">
// Aubaine DS Button (07/10, management look): filled = the primary action (ink, one per region); tinted and gray =
// secondary (outlined, grey text); plain = text action. small 28 / medium 36 / large 40 (44 below 1024px).
// tone="destructive": outlined red (filled red only as a dialog's confirm action).
withDefaults(
  defineProps<{
    variant?: 'filled' | 'tinted' | 'gray' | 'plain'
    size?: 'small' | 'medium' | 'large'
    tone?: 'default' | 'destructive'
    type?: 'button' | 'submit'
    fullWidth?: boolean
    disabled?: boolean
    loading?: boolean
  }>(),
  { variant: 'filled', size: 'medium', tone: 'default', type: 'button' },
)

defineEmits<{ click: [event: MouseEvent] }>()
</script>

<template>
  <button
    :type="type"
    class="ui-button"
    :class="[`ui-button--${variant}`, `ui-button--${size}`, { 'ui-button--destructive': tone === 'destructive', 'ui-button--full': fullWidth }]"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="ui-button__spinner" aria-hidden="true"></span>
    <slot name="icon" />
    <slot />
  </button>
</template>

<style scoped>
.ui-button {
  --tint: var(--accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid transparent;
  border-radius: var(--radius-control);
  font-family: var(--font-sans);
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  letter-spacing: 0;
  cursor: pointer;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  transition:
    opacity var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);
}
.ui-button :deep(svg) {
  width: 14px;
  height: 14px;
}
.ui-button:disabled {
  opacity: 0.5;
  cursor: default;
}
.ui-button[aria-busy='true'] {
  opacity: 0.6;
}
.ui-button--small {
  height: 28px;
  min-width: 28px;
  padding: 0 10px;
  font-size: var(--type-footnote-size);
}
.ui-button--medium {
  height: var(--control-height);
  min-width: var(--control-height);
  padding: 0 16px;
}
.ui-button--large {
  height: 40px;
  min-width: 40px;
  padding: 0 20px;
}
@media (max-width: 1023px) {
  .ui-button--small {
    height: 36px;
    min-width: 36px;
  }
  .ui-button--large {
    height: 44px;
  }
}
.ui-button--filled {
  background: var(--tint);
  border-color: var(--tint);
  color: var(--on-accent);
}
.ui-button--filled:hover:not(:disabled) {
  opacity: 0.86;
}
.ui-button--tinted,
.ui-button--gray {
  background: var(--surface-card);
  border-color: var(--opaque-separator);
  color: var(--text-secondary);
}
.ui-button--tinted:hover:not(:disabled),
.ui-button--gray:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--text-primary);
}
.ui-button--plain {
  background: transparent;
  color: var(--tint);
  padding: 0 4px;
  min-width: 0;
}
.ui-button--plain:hover:not(:disabled) {
  text-decoration: underline;
}
.ui-button--destructive {
  --tint: var(--danger);
}
.ui-button--destructive.ui-button--filled {
  color: #ffffff;
}
.ui-button--destructive.ui-button--tinted,
.ui-button--destructive.ui-button--gray {
  color: var(--danger);
  border-color: var(--danger-border);
}
.ui-button--destructive.ui-button--tinted:hover:not(:disabled),
.ui-button--destructive.ui-button--gray:hover:not(:disabled) {
  color: var(--danger);
  background: var(--tint-danger-bg);
}
.ui-button--full {
  width: 100%;
}
.ui-button__spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: aub-spin 0.8s linear infinite;
}
</style>
